import { lazy, Suspense, useCallback, useEffect, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { gsap, reducedMotion, unlockHeroRefresh, useIdleMounted, cinematicPinType, cinematicScrub, WebGLBoundary } from './core';
import type { RouteCardInfo, RouteInteract } from './EarthCanvas';

/* ============================================================
   Chapters 1 & 2 — The Beginning / Discovery.
   One pinned WebGL scene: darkness → stars → Earth emerges →
   sunrise sweeps the terminator → camera flies in → travel
   routes draw themselves. Scroll drives everything.
   The three.js scene itself is a lazy chunk (EarthCanvas).
   ============================================================ */

const EarthCanvas = lazy(() => import('./EarthCanvas'));

/** Desktop only: warm the Earth texture cache in parallel with the
 *  idle-gated WebGL chunk. On throttled mobile connections the prefetch
 *  competes with critical resources and hurts LCP, so it's skipped there. */
function usePrefetchEarthTextures() {
  useEffect(() => {
    if (window.innerWidth < 768) return;
    ['/textures/earth-day-2k.jpg', '/textures/earth-night-2k.jpg', '/textures/earth-clouds.png'].forEach(
      (href) => {
        if (document.querySelector(`link[href="${href}"]`)) return;
        const l = document.createElement('link');
        l.rel = 'prefetch';
        l.as = 'image';
        l.href = href;
        document.head.appendChild(l);
      }
    );
  }, []);
}

const DISCOVERY_COPY = [
  {
    pos: 'pos-l',
    title: 'One world. One home for every trip.',
    body: 'Every destination, itinerary and memory — organized in a single beautiful place.',
  },
  {
    pos: 'pos-r',
    title: 'Watch your routes come alive.',
    body: 'Flights, trains and road trips connect across the globe as you plan.',
  },
  {
    pos: 'pos-c',
    title: 'Where to next?',
    body: 'Rovera learns how you love to travel — and lights the way.',
  },
];

export default function EarthSequence() {
  const root = useRef<HTMLDivElement>(null);
  const progress = useRef(0);
  const mouse = useRef({ x: 0, y: 0 });
  const activeRef = useRef(true);
  const rm = useMemo(() => reducedMotion(), []);
  const glReady = useIdleMounted();
  usePrefetchEarthTextures();

  // Floating route/destination card: written to directly (no React state) so
  // hovering the globe never triggers a re-render — GPU-only transform writes.
  const cardRef = useRef<HTMLDivElement>(null);
  const cardIconRef = useRef<HTMLSpanElement>(null);
  const cardTitleRef = useRef<HTMLElement>(null);
  const cardMetaRef = useRef<HTMLElement>(null);
  const cardStatusRef = useRef<HTMLSpanElement>(null);
  const cardTagsRef = useRef<HTMLUListElement>(null);

  const positionCard = useCallback((x: number, y: number) => {
    const el = cardRef.current;
    if (!el) return;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const cw = el.offsetWidth || 270;
    const ch = el.offsetHeight || 170;
    let left = x + 20;
    let top = y + 20;
    if (left + cw > vw - 16) left = x - cw - 20;
    if (top + ch > vh - 16) top = y - ch - 20;
    left = Math.max(16, left);
    top = Math.max(16, top);
    el.style.transform = `translate(${left}px, ${top}px)`;
  }, []);

  const showCard = useCallback(
    (info: RouteCardInfo, x: number, y: number) => {
      if (cardIconRef.current) cardIconRef.current.textContent = info.icon;
      if (cardTitleRef.current) cardTitleRef.current.textContent = info.title;
      if (cardMetaRef.current) cardMetaRef.current.textContent = info.meta;
      if (cardStatusRef.current) {
        cardStatusRef.current.textContent = info.statusLabel;
        cardStatusRef.current.dataset.status = info.status;
      }
      if (cardTagsRef.current) {
        cardTagsRef.current.replaceChildren(
          ...info.tags.map((t) => {
            const li = document.createElement('li');
            li.textContent = t;
            return li;
          })
        );
      }
      cardRef.current?.classList.add('visible');
      positionCard(x, y);
    },
    [positionCard]
  );

  const hideCard = useCallback(() => {
    cardRef.current?.classList.remove('visible');
  }, []);

  const interact: RouteInteract = useMemo(
    () => ({ onHover: showCard, onMove: positionCard, onLeave: hideCard }),
    [showCard, positionCard, hideCard]
  );

  // pause the GL loop work once the sequence scrolls away — keep Canvas
  // frameloop stable (toggling the prop remounts/clears the buffer → flicker)
  useEffect(() => {
    const el = root.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([e]) => {
        activeRef.current = e.isIntersecting;
      },
      { rootMargin: '10% 0px' },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // cursor → subtle globe/star response (smoothed inside the GL loop)
  useEffect(() => {
    if (rm) return;
    const onMove = (e: MouseEvent) => {
      mouse.current.x = (e.clientX / window.innerWidth - 0.5) * 2;
      mouse.current.y = (e.clientY / window.innerHeight - 0.5) * 2;
    };
    window.addEventListener('mousemove', onMove, { passive: true });
    return () => window.removeEventListener('mousemove', onMove);
  }, [rm]);

  useEffect(() => {
    const el = root.current;
    if (!el) return;

    if (rm) {
      progress.current = 0.34; // sunlit earth, no motion
      el.classList.add('cine-hero-settled');
      unlockHeroRefresh(80);
      const ctx = gsap.context(() => {
        gsap.set(
          ['.cine-earth-title > *', '.cine-earth-title', '.cine-earth-copy', '.cine-hero-stats', '.cine-scroll-hint'],
          { autoAlpha: 1, y: 0, clearProps: 'transform' },
        );
      }, root);
      return () => ctx.revert();
    }

    // One-shot GSAP entrance — CSS keyframes restart whenever ScrollTrigger
    // refresh re-pins this node, which looked like the headline "re-rendering".
    const unlockTimer = window.setTimeout(() => unlockHeroRefresh(120), 3200);
    const ctx = gsap.context(() => {
      gsap.set(['.cine-earth-title > *', '.cine-hero-stats > *', '.cine-scroll-hint > *'], {
        autoAlpha: 0,
        y: 42,
      });
      gsap.set('.cine-earth-copy', { autoAlpha: 0 });

      const entrance = gsap.timeline({
        defaults: { ease: 'power3.out' },
        onComplete: () => {
          el.classList.add('cine-hero-settled');
          // Pin must refresh first, then every section trigger recalculates.
          unlockHeroRefresh(200);
        },
      });
      entrance
        .to('.cine-earth-title > *', { autoAlpha: 1, y: 0, duration: 1.05, stagger: 0.2 }, 0.05)
        .to('.cine-hero-stats > *', { autoAlpha: 1, y: 0, duration: 0.85, stagger: 0.08 }, 0.95)
        .to('.cine-scroll-hint > *', { autoAlpha: 1, y: 0, duration: 0.7 }, 1.35);

      const tl = gsap.timeline({
        defaults: { ease: 'none' },
        scrollTrigger: {
          trigger: root.current,
          start: 'top top',
          end: '+=430%',
          scrub: cinematicScrub(0.55),
          pin: true,
          // Desktop + Lenis → transform. Real phones → fixed (transform pins
          // fight native touch momentum and shake the globe).
          pinType: cinematicPinType(),
          anticipatePin: 1,
          invalidateOnRefresh: false,
          // Pins change document spacing — refresh BEFORE other section triggers.
          refreshPriority: 1,
          onUpdate: (st) => {
            progress.current = st.progress;
          },
        },
      });
      // Chapter 1 — scroll fades the settled hero away
      tl.to('.cine-earth-title', { y: -14, scale: 0.985, duration: 0.14, ease: 'none' }, 0)
        .to('.cine-earth-title', { autoAlpha: 0, y: -52, scale: 0.96, duration: 0.09 }, 0.14)
        .to('.cine-hero-stats', { y: -22, duration: 0.12, ease: 'none' }, 0)
        .to('.cine-hero-stats', { autoAlpha: 0, y: 26, duration: 0.08 }, 0.12)
        .to('.cine-scroll-hint', { autoAlpha: 0, duration: 0.05 }, 0.1);
      // Chapter 2 — discovery copy
      const copies = gsap.utils.toArray<HTMLElement>('.cine-earth-copy');
      const beats = [0.42, 0.6, 0.78];
      copies.forEach((copyEl, i) => {
        tl.fromTo(copyEl, { autoAlpha: 0, y: 44 }, { autoAlpha: 1, y: 0, duration: 0.07 }, beats[i])
          .to(copyEl, { autoAlpha: 0, y: -36, duration: 0.06 }, beats[i] + 0.12);
      });
      // hand-off: settle to black so the next chapter emerges from darkness
      tl.fromTo('.cine-earth-exit', { autoAlpha: 0 }, { autoAlpha: 1, duration: 0.06 }, 0.94);
    }, root);
    return () => {
      window.clearTimeout(unlockTimer);
      ctx.revert();
    };
  }, [rm]);

  return (
    <div
      className="cine-earth-wrap"
      ref={root}
      role="region"
      aria-label="The world is waiting — discover it with Rovera"
    >
      <div className="cine-earth-bg" aria-hidden />
      <div className="cine-earth-canvas" aria-hidden>
        {glReady && (
          <WebGLBoundary>
            <Suspense fallback={null}>
              <EarthCanvas
                progress={progress}
                mouse={mouse}
                active={activeRef}
                reducedMotion={rm}
                interact={interact}
              />
            </Suspense>
          </WebGLBoundary>
        )}
      </div>
      <div className="cine-earth-vignette" aria-hidden />

      {/* Route/destination hover card — imperatively positioned, see showCard/positionCard above */}
      <div className="cine-route-card glass" ref={cardRef} aria-hidden>
        <div className="rc-top">
          <span className="rc-icon" ref={cardIconRef} />
          <b className="rc-title" ref={cardTitleRef} />
        </div>
        <small className="rc-meta" ref={cardMetaRef} />
        <span className="rc-status" ref={cardStatusRef} />
        <ul className="rc-tags" ref={cardTagsRef} />
      </div>

      <div className="cine-earth-title">
        <span className="cine-kicker">AI-powered travel</span>
        <h1>
          <span className="line">The World</span>
          <span className="line cine-grad-text">Awaits.</span>
        </h1>
        <p className="cine-hero-sub">Plan together. Travel further. Remember everything.</p>
        <div className="cine-hero-cta">
          {/* No magnetic pull here — GSAP x/y fights .cine-btn:hover transform and flickers. */}
          <Link to="/register" className="cine-btn cine-btn-primary cine-btn-lg">
            Start Planning
          </Link>
          <a href="#pricing" className="cine-btn cine-btn-outline">
            See plans
          </a>
        </div>
      </div>

      <aside className="cine-hero-stats glass" aria-label="Your adventures at a glance">
        <span className="label">Your adventures</span>
        <div className="row">
          <div>
            <b>24</b>
            <small>Trips</small>
          </div>
          <div>
            <b>18</b>
            <small>Countries</small>
          </div>
          <div>
            <b>126</b>
            <small>Days</small>
          </div>
        </div>
        <svg viewBox="0 0 220 34" fill="none" aria-hidden>
          <path
            d="M2 26 C 30 30, 48 14, 76 18 S 128 30, 156 20 S 200 8, 218 10"
            stroke="#37e3c8"
            strokeWidth="2.5"
            strokeLinecap="round"
            opacity="0.9"
          />
          <circle cx="218" cy="10" r="4" fill="#37e3c8" />
        </svg>
      </aside>

      {DISCOVERY_COPY.map((c) => (
        <div key={c.pos} className={`cine-earth-copy ${c.pos}`}>
          <h2>{c.title}</h2>
          <p>{c.body}</p>
        </div>
      ))}

      <div className="cine-scroll-hint" aria-hidden>
        <div className="wheel" />
        <span>Scroll</span>
      </div>
      <div className="cine-earth-exit" aria-hidden />
    </div>
  );
}
